import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type InitiateConnectOutput = OutputRouter['stripe']['initiateConnect']
export type DisconnectOutput = OutputRouter['stripe']['disconnect']
export type StripeAccountDetails = OutputRouter['stripe']['getAccountDetails']
export type UpdateProcessorOutput = OutputRouter['stripe']['updateProcessor']
export type DeleteProcessorOutput = OutputRouter['stripe']['deleteProcessor']
export type CreateSubscriptionOutput = OutputRouter['stripe']['createSubscription']
export type UnsubscribeOutput = OutputRouter['stripe']['unsubscribe']
export type CustomerPortalSessionOutput = OutputRouter['stripe']['createCustomerPortalSession']
export type ResumeSubscriptionOutput = OutputRouter['stripe']['resumeSubscription']
export type PaymentMethodsOutput = OutputRouter['stripe']['getPaymentMethods']
export type CustomerPortalPaymentMethodSessionOutput = OutputRouter['stripe']['createCustomerPortalPaymentMethodSession']

// Input types
export type InitiateConnectInput = InputRouter['stripe']['initiateConnect']
export type DisconnectInput = InputRouter['stripe']['disconnect']
export type GetAccountDetailsInput = InputRouter['stripe']['getAccountDetails']
export type UpdateProcessorInput = InputRouter['stripe']['updateProcessor']
export type DeleteProcessorInput = InputRouter['stripe']['deleteProcessor']
export type CreateSubscriptionInput = InputRouter['stripe']['createSubscription']
export type UnsubscribeInput = InputRouter['stripe']['unsubscribe']
export type CreateCustomerPortalSessionInput = InputRouter['stripe']['createCustomerPortalSession']
export type ResumeSubscriptionInput = InputRouter['stripe']['resumeSubscription']
export type GetPaymentMethodsInput = InputRouter['stripe']['getPaymentMethods']
export type CreateCustomerPortalPaymentMethodSessionInput = InputRouter['stripe']['createCustomerPortalPaymentMethodSession']

// Query hooks
export const useStripeAccountDetails = (accountId: string) => {
    return trpc.stripe.getAccountDetails.useQuery(
        { accountId },
        { enabled: !!accountId }
    )
}

export const usePaymentMethods = () => {
    return trpc.stripe.getPaymentMethods.useQuery({})
}

// Mutation hooks
export const useInitiateStripeConnect = () => {
    return trpc.stripe.initiateConnect.useMutation()
}

export const useDisconnectStripe = () => {
    const utils = trpc.useUtils()

    return trpc.stripe.disconnect.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate()
            utils.processors.getById.invalidate()
        },
    })
}

export const useUpdateStripeProcessor = () => {
    const utils = trpc.useUtils()

    return trpc.stripe.updateProcessor.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate()
            utils.processors.getById.invalidate()
        },
    })
}

export const useDeleteStripeProcessor = () => {
    const utils = trpc.useUtils()

    return trpc.stripe.deleteProcessor.useMutation({
        onSuccess: () => {
            utils.processors.list.invalidate()
        },
    })
}

export const useCreateStripeSubscription = () => {
    return trpc.stripe.createSubscription.useMutation()
}

export const useUnsubscribeFromPremium = () => {
    return trpc.stripe.unsubscribe.useMutation()
}

export const useCreateCustomerPortalSession = () => {
    return trpc.stripe.createCustomerPortalSession.useMutation()
}

export const useResumeSubscription = () => {
    return trpc.stripe.resumeSubscription.useMutation()
}

export const useCreateCustomerPortalPaymentMethodSession = () => {
    return trpc.stripe.createCustomerPortalPaymentMethodSession.useMutation()
}

/**
 * Creates a SetupIntent for embedded Stripe Elements card capture.
 * Returns the client secret + publishable key consumed by the embedded
 * card component (see EmbeddedCardSetup).
 */
export const useCreateSetupIntent = () => {
    return trpc.stripe.createSetupIntent.useMutation()
}

/**
 * Attaches a confirmed payment method and marks it the customer default so the
 * billing gate unblocks immediately (before the webhook fires).
 */
export const useSetDefaultPaymentMethod = () => {
    return trpc.stripe.setDefaultPaymentMethod.useMutation()
}

